import { useState } from "react";
import clsx from "clsx";

// Interfaces
import { ISideTreeChild, ISideTreeCommons, TGetTreeChildStyleArgs } from "../interfaces";

// Helpers
import { getPrecisionLevel } from "../helpers";

// MUI
import { TreeItem } from "@mui/x-tree-view";

// Components
import SideTreeContent from "../SideTreeChildContent";
import SideTreeIcon from "../SideTreeIcon";

// Internal helpers
const getIcon = (props: object) => <SideTreeIcon {...props} />;

const getSlots = (isParent: boolean, props: ISideTreeCommons) => {
	if (!isParent) {
		return undefined;
	}

	const { disabled = false, selected = false } = props;

	return {
		expandIcon: () => getIcon({ disabled, selected }),
		collapseIcon: () => getIcon({ collapse: true, disabled }),
	};
};

const getTreeChildStyle = (args: TGetTreeChildStyleArgs) => {
	const { selectedItem, isAdditionalSelected, isDisabled, controlled } = args;
	return {
		"!rounded-[25px] !w-[230px]": true,
		"!bg-[var(--primary-color-opacity-20)]": selectedItem && isAdditionalSelected && !isDisabled,
		"hover:!bg-[var(--primary-color-opacity-20)]": !selectedItem && !isDisabled,
		"hover:!bg-primary !bg-primary": selectedItem && !isAdditionalSelected && !isDisabled,
		"hover:!bg-transparent !bg-transparent ": isDisabled || controlled,
		"!cursor-default": isDisabled,
	};
};

export default function SideTreeChild(props: Readonly<ISideTreeChild>) {
	const {
		children = null,
		parent,
		parentId = "",
		showBy = "title",
		level = 0,
		disabled = false,
		selected = false,
		hasSelectedLeaf = false,
		isAdditionalSelected = false,
	} = props;

	// States
	const [hovered, setHovered] = useState(false);
	const isHoverPropagable = Boolean(parent?.information);

	// Handlers
	const handleMouseEnter = () => {
		if (isHoverPropagable) {
			setHovered(true);
		}
	};
	const handleMouseLeave = () => {
		if (isHoverPropagable) {
			setHovered(false);
		}
	};

	const isDisabled = disabled || parent?.disabled;
	const isEmptyRootParent = Boolean(parent?.root) && !children;
	const isParent = Boolean(children) || isEmptyRootParent;
	const addLevel = isParent ? 4 : 2;
	const slots = getSlots(isParent, { disabled: isDisabled, selected: hasSelectedLeaf });
	const title = parent?.[showBy as keyof object] ?? "";
	const controlled = parent?.["controlled" as keyof object] ?? false;
	const selectedItem = !controlled && selected;

	return (
		<TreeItem
			itemId={parentId}
			label={
				<SideTreeContent
					{...parent}
					isParent={isParent}
					title={title}
					disabled={isDisabled}
					selected={selectedItem && !isAdditionalSelected}
					hovered={hovered}
					controlled={controlled}
				/>
			}
			disabled={isDisabled}
			ContentProps={{
				className: clsx(getTreeChildStyle({ selectedItem, isAdditionalSelected, isDisabled, controlled })),
				style: { paddingLeft: getPrecisionLevel(level + addLevel, !isParent, isEmptyRootParent) },
			}}
			slots={slots}
			onMouseEnter={handleMouseEnter}
			onMouseLeave={handleMouseLeave}
			sx={{
				"&.MuiTreeItem-root": {
					"& .Mui-focused": {
						backgroundColor: "transparent",
					},
				},
			}}
		>
			{children}
		</TreeItem>
	);
}
